import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { fetchAllPublicDistributionBatches } from 'helpers/distributionBatch.api';
import { getPublicMicrojobPayoutLedger } from 'helpers/microjob.api';
import { fetchAllPublicRevenueItems, getPublicRevenueItems } from 'helpers/paymentRequest.api';
import { getPublicRevenueClaimSummary } from 'helpers/revenueClaim.api';
import {
  colors,
  fieldBlockStyles,
  fieldLabelStyles,
  filterToolbarSx,
  formFieldStyles,
  makeSelectSx,
  pageTitleSx,
  previewSummaryCardSx,
  previewSummaryGridSx,
  previewSummaryLabelSx,
  previewSummaryValueSx,
  selectMenuProps,
  subTabSx,
  tabHeaderRowSx,
  tableAlertErrorSx,
  tableCardStyles,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import { buildPaginationItems, formatDateTime, REVENUE_ITEMS_PAGE_SIZE } from '../utils/revenueItems.utils';
import {
  aggregateMicrojobRecipientAllocations,
  aggregateRecipientAllocations,
  computeTransparencyStats,
  flattenCombinedPayoutLedgerRows,
  mergeRecipientAllocationRows,
  mergeRecipientPayoutSummaries,
} from '../utils/transparency.utils';
import PaymentsPagination from './PaymentsPagination';
import RevenueItemRow from './RevenueItemRow';

const formatUsdt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '0');
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 });
};

const VAULT_WALLET_SOURCE_LABEL = 'Order Handler · LP';
const MICROJOB_WALLET_SOURCE_LABEL = 'Microjob';

const hasVaultWalletActivity = (row) =>
  Number(row.vaultAllocatedTotal) > 0 || Number(row.vaultClaimed) > 0;

const hasMicrojobWalletActivity = (row) =>
  Number(row.microjobAllocatedTotal) > 0 || Number(row.microjobClaimed) > 0;

const vaultWalletTypeLabels = (row) => {
  const labels = (row.recipientTypeLabels || '')
    .split(', ')
    .filter((label) => label && label !== MICROJOB_WALLET_SOURCE_LABEL);
  return labels.length ? labels.join(', ') : '—';
};

const walletAddressCellSx = {
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  wordBreak: 'break-all',
  lineHeight: 1.45,
};

const TransparencySection = ({ currentDao }) => {
  const [view, setView] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [batches, setBatches] = useState([]);
  const [microjobPayouts, setMicrojobPayouts] = useState([]);
  const [stats, setStats] = useState(null);

  const [ledgerStatus, setLedgerStatus] = useState('');
  const [ledgerRevenueType, setLedgerRevenueType] = useState('');
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPagination, setLedgerPagination] = useState({
    page: 1,
    limit: REVENUE_ITEMS_PAGE_SIZE,
    totalItems: 0,
    totalPages: 1,
  });
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerItems, setLedgerItems] = useState([]);

  const [payoutRecipient, setPayoutRecipient] = useState('');
  const [payoutRecipientType, setPayoutRecipientType] = useState('');
  const [payoutSubView, setPayoutSubView] = useState(0);
  const [payoutWalletPage, setPayoutWalletPage] = useState(1);
  const [payoutMicrojobWalletPage, setPayoutMicrojobWalletPage] = useState(1);
  const [payoutLineItemsPage, setPayoutLineItemsPage] = useState(1);
  const [claimSummaries, setClaimSummaries] = useState([]);
  const [claimSummaryLoading, setClaimSummaryLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!currentDao?._id) return;
    setLoading(true);
    setError('');
    try {
      const [items, batchList, microjobLedgerRes] = await Promise.all([
        fetchAllPublicRevenueItems({ daoId: currentDao._id, sortOrder: 'desc' }),
        fetchAllPublicDistributionBatches({ daoId: currentDao._id }),
        getPublicMicrojobPayoutLedger(currentDao._id).catch(() => ({ data: [], summary: null })),
      ]);
      setBatches(batchList);
      setMicrojobPayouts(microjobLedgerRes?.data || []);
      setStats(computeTransparencyStats(items, batchList, microjobLedgerRes?.summary));
    } catch (err) {
      setError(err?.message || 'Failed to load transparency data');
    } finally {
      setLoading(false);
    }
  }, [currentDao?._id]);

  const loadLedger = useCallback(async () => {
    if (!currentDao?._id) return;
    setLedgerLoading(true);
    try {
      const filters = {
        daoId: currentDao._id,
        page: ledgerPage,
        limit: REVENUE_ITEMS_PAGE_SIZE,
        sortOrder: 'desc',
      };
      if (ledgerStatus) filters.status = ledgerStatus;
      if (ledgerRevenueType) filters.revenueType = ledgerRevenueType;
      const res = await getPublicRevenueItems(filters);
      setLedgerItems(res?.data || []);
      setLedgerPagination({
        page: res?.pagination?.page || ledgerPage,
        limit: res?.pagination?.limit || REVENUE_ITEMS_PAGE_SIZE,
        totalItems: res?.pagination?.totalItems || 0,
        totalPages: res?.pagination?.totalPages || 1,
      });
    } catch (err) {
      setLedgerItems([]);
      setLedgerPagination({ page: 1, limit: REVENUE_ITEMS_PAGE_SIZE, totalItems: 0, totalPages: 1 });
      setError(err?.message || 'Failed to load revenue ledger');
    } finally {
      setLedgerLoading(false);
    }
  }, [currentDao?._id, ledgerPage, ledgerStatus, ledgerRevenueType]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (view !== 1 || !currentDao?._id) return undefined;
    setLedgerLoading(true);
    const timer = setTimeout(() => loadLedger(), 350);
    return () => clearTimeout(timer);
  }, [view, ledgerPage, ledgerStatus, ledgerRevenueType, currentDao?._id, loadLedger]);

  const loadClaimSummary = useCallback(async () => {
    if (!currentDao?._id) return;
    setClaimSummaryLoading(true);
    try {
      const res = await getPublicRevenueClaimSummary(currentDao._id);
      setClaimSummaries(res?.data || []);
    } catch {
      setClaimSummaries([]);
    } finally {
      setClaimSummaryLoading(false);
    }
  }, [currentDao?._id]);

  useEffect(() => {
    if (view !== 2 || !currentDao?._id) return;
    loadClaimSummary();
  }, [view, currentDao?._id, loadClaimSummary]);

  const allocations = useMemo(
    () =>
      mergeRecipientAllocationRows(
        aggregateRecipientAllocations(batches, {
          recipientFilter: payoutRecipient,
          recipientType: payoutRecipientType,
        }),
        aggregateMicrojobRecipientAllocations(microjobPayouts, {
          recipientFilter: payoutRecipient,
          recipientType: payoutRecipientType,
        }),
      ),
    [batches, microjobPayouts, payoutRecipient, payoutRecipientType],
  );

  const recipientRowsAll = useMemo(
    () => mergeRecipientPayoutSummaries(allocations, claimSummaries, {}),
    [allocations, claimSummaries],
  );

  const recipientRowsFiltered = useMemo(() => {
    const needle = payoutRecipient.trim().toLowerCase();
    if (!needle) return recipientRowsAll;
    return recipientRowsAll.filter((r) => (r.recipientAddress || '').toLowerCase().includes(needle));
  }, [recipientRowsAll, payoutRecipient]);

  const showVaultWalletTable = !payoutRecipientType || payoutRecipientType !== 'microjob';
  const showMicrojobWalletTable = !payoutRecipientType || payoutRecipientType === 'microjob';

  const vaultWalletRows = useMemo(
    () => recipientRowsFiltered.filter(hasVaultWalletActivity),
    [recipientRowsFiltered],
  );

  const microjobWalletRows = useMemo(
    () => recipientRowsFiltered.filter(hasMicrojobWalletActivity),
    [recipientRowsFiltered],
  );

  const payoutWalletPagination = useMemo(() => {
    const totalItems = vaultWalletRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / REVENUE_ITEMS_PAGE_SIZE));
    const page = Math.min(payoutWalletPage, totalPages);
    return { page, limit: REVENUE_ITEMS_PAGE_SIZE, totalItems, totalPages };
  }, [vaultWalletRows.length, payoutWalletPage]);

  const payoutMicrojobWalletPagination = useMemo(() => {
    const totalItems = microjobWalletRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / REVENUE_ITEMS_PAGE_SIZE));
    const page = Math.min(payoutMicrojobWalletPage, totalPages);
    return { page, limit: REVENUE_ITEMS_PAGE_SIZE, totalItems, totalPages };
  }, [microjobWalletRows.length, payoutMicrojobWalletPage]);

  const recipientRowsPage = useMemo(() => {
    const start = (payoutWalletPagination.page - 1) * REVENUE_ITEMS_PAGE_SIZE;
    return vaultWalletRows.slice(start, start + REVENUE_ITEMS_PAGE_SIZE);
  }, [vaultWalletRows, payoutWalletPagination.page]);

  const microjobRecipientRowsPage = useMemo(() => {
    const start = (payoutMicrojobWalletPagination.page - 1) * REVENUE_ITEMS_PAGE_SIZE;
    return microjobWalletRows.slice(start, start + REVENUE_ITEMS_PAGE_SIZE);
  }, [microjobWalletRows, payoutMicrojobWalletPagination.page]);

  const payoutRows = useMemo(
    () =>
      flattenCombinedPayoutLedgerRows(
        batches,
        microjobPayouts,
        payoutRecipient,
        payoutRecipientType,
      ),
    [batches, microjobPayouts, payoutRecipient, payoutRecipientType],
  );

  const payoutLineItemsPagination = useMemo(() => {
    const totalItems = payoutRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / REVENUE_ITEMS_PAGE_SIZE));
    const pageNum = Math.min(payoutLineItemsPage, totalPages);
    return { page: pageNum, limit: REVENUE_ITEMS_PAGE_SIZE, totalItems, totalPages };
  }, [payoutRows.length, payoutLineItemsPage]);

  const payoutRowsPage = useMemo(() => {
    const start = (payoutLineItemsPagination.page - 1) * REVENUE_ITEMS_PAGE_SIZE;
    return payoutRows.slice(start, start + REVENUE_ITEMS_PAGE_SIZE);
  }, [payoutRows, payoutLineItemsPagination.page]);

  const payoutLineItemsPaginationItems = useMemo(
    () => buildPaginationItems(payoutLineItemsPagination.page, payoutLineItemsPagination.totalPages),
    [payoutLineItemsPagination.page, payoutLineItemsPagination.totalPages],
  );

  const payoutLineItemsPaginationLabel = useMemo(() => {
    if (!payoutLineItemsPagination.totalItems) {
      return loading ? 'Loading payout line items...' : 'No executed payout rows yet';
    }
    const start = (payoutLineItemsPagination.page - 1) * REVENUE_ITEMS_PAGE_SIZE + 1;
    const end = Math.min(start + payoutRowsPage.length - 1, payoutLineItemsPagination.totalItems);
    return `Showing ${start}-${end} of ${payoutLineItemsPagination.totalItems} payout lines`;
  }, [loading, payoutLineItemsPagination, payoutRowsPage.length]);

  const payoutWalletPaginationItems = useMemo(
    () => buildPaginationItems(payoutWalletPagination.page, payoutWalletPagination.totalPages),
    [payoutWalletPagination.page, payoutWalletPagination.totalPages],
  );

  const payoutMicrojobWalletPaginationItems = useMemo(
    () =>
      buildPaginationItems(
        payoutMicrojobWalletPagination.page,
        payoutMicrojobWalletPagination.totalPages,
      ),
    [payoutMicrojobWalletPagination.page, payoutMicrojobWalletPagination.totalPages],
  );

  const payoutWalletPaginationLabel = useMemo(() => {
    if (!payoutWalletPagination.totalItems) {
      return claimSummaryLoading ? 'Loading recipient data...' : 'No Vault wallets found';
    }
    const start = (payoutWalletPagination.page - 1) * REVENUE_ITEMS_PAGE_SIZE + 1;
    const end = Math.min(start + recipientRowsPage.length - 1, payoutWalletPagination.totalItems);
    return `Showing ${start}-${end} of ${payoutWalletPagination.totalItems} Vault wallets`;
  }, [claimSummaryLoading, payoutWalletPagination, recipientRowsPage.length]);

  const payoutMicrojobWalletPaginationLabel = useMemo(() => {
    if (!payoutMicrojobWalletPagination.totalItems) {
      return claimSummaryLoading ? 'Loading recipient data...' : 'No Microjob wallets found';
    }
    const start = (payoutMicrojobWalletPagination.page - 1) * REVENUE_ITEMS_PAGE_SIZE + 1;
    const end = Math.min(
      start + microjobRecipientRowsPage.length - 1,
      payoutMicrojobWalletPagination.totalItems,
    );
    return `Showing ${start}-${end} of ${payoutMicrojobWalletPagination.totalItems} Microjob wallets`;
  }, [claimSummaryLoading, payoutMicrojobWalletPagination, microjobRecipientRowsPage.length]);

  const renderTableLoadingRow = (colSpan, message = 'Loading...') => (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 5 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={24} sx={{ color: colors.accent }} />
          <Typography sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif' }}>
            {message}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const ledgerPaginationItems = useMemo(
    () => buildPaginationItems(ledgerPagination.page || 1, ledgerPagination.totalPages || 1),
    [ledgerPagination.page, ledgerPagination.totalPages],
  );

  const ledgerPaginationLabel = useMemo(() => {
    if (!ledgerPagination.totalItems) {
      return ledgerLoading ? 'Loading revenue ledger...' : 'No revenue items found';
    }
    const start = (ledgerPagination.page - 1) * ledgerPagination.limit + 1;
    const end = Math.min(start + ledgerItems.length - 1, ledgerPagination.totalItems);
    return `Showing ${start}-${end} of ${ledgerPagination.totalItems} revenue items`;
  }, [ledgerLoading, ledgerPagination, ledgerItems.length]);

  const renderDashboard = () => {
    if (loading) {
      return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress sx={{ color: colors.accent }} />
        </Box>
      );
    }

    const cards = [
      { label: 'Total revenue (confirmed)', value: `${formatUsdt(stats?.totalRevenueAllTime)} USDT`, accent: true },
      { label: 'Revenue items', value: String(stats?.revenueItemCount ?? 0) },
      { label: 'Pending cycles', value: String(stats?.pendingCycles ?? 0) },
      { label: 'Executed payouts', value: String(stats?.executedPayouts ?? 0) },
      {
        label: 'Payout volume',
        value: `${formatUsdt(stats?.totalPayoutVolume)} USDT`,
      },
      {
        label: 'Revenue marked distributed',
        value: `${formatUsdt(stats?.totalDistributedRevenue)} USDT`,
      },
    ];

    return (
      <Box sx={{ ...previewSummaryGridSx, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' } }}>
        {cards.map((c) => (
          <Box key={c.label} sx={previewSummaryCardSx}>
            <Typography sx={previewSummaryLabelSx}>{c.label}</Typography>
            <Typography sx={{ ...previewSummaryValueSx, color: c.accent ? colors.accent : colors.textPrimary }}>
              {c.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderLedgerFilters = () => (
    <Box sx={filterToolbarSx}>
      <Box sx={{ ...fieldBlockStyles, minWidth: 160 }}>
        <Typography sx={fieldLabelStyles}>Status</Typography>
        <Select
          value={ledgerStatus}
          onChange={(e) => {
            setLedgerStatus(e.target.value);
            setLedgerPage(1);
          }}
          displayEmpty
          fullWidth
          renderValue={(v) => {
            if (!v) return 'All statuses';
            return v.replace(/_/g, ' ');
          }}
          sx={makeSelectSx(false, !!ledgerStatus)}
          MenuProps={selectMenuProps}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="confirmed">Confirmed</MenuItem>
          <MenuItem value="distributed">Distributed</MenuItem>
          <MenuItem value="invalid">Invalid</MenuItem>
          <MenuItem value="refunded">Refunded</MenuItem>
        </Select>
      </Box>
      <Box sx={{ ...fieldBlockStyles, minWidth: 160 }}>
        <Typography sx={fieldLabelStyles}>Revenue type</Typography>
        <Select
          value={ledgerRevenueType}
          onChange={(e) => {
            setLedgerRevenueType(e.target.value);
            setLedgerPage(1);
          }}
          displayEmpty
          fullWidth
          renderValue={(v) => (!v ? 'All types' : v.replace(/_/g, ' '))}
          sx={makeSelectSx(false, !!ledgerRevenueType)}
          MenuProps={selectMenuProps}
        >
          <MenuItem value="">All types</MenuItem>
          <MenuItem value="service_revenue">Service</MenuItem>
          <MenuItem value="product_revenue">Product</MenuItem>
        </Select>
      </Box>
    </Box>
  );

  const renderRevenueLedger = () => (
    <>
      {renderLedgerFilters()}
      <Box sx={tableCardStyles}>
        <TableContainer component={Box} sx={tableContainerStyles}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Revenue Type</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>On-chain request ID</TableCell>
                <TableCell>Tx Hash</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledgerLoading ? (
                renderTableLoadingRow(8, 'Loading revenue ledger...')
              ) : ledgerItems.length ? (
                ledgerItems.map((item) => (
                  <RevenueItemRow key={item._id} item={item} showDao={false} showOnchainRequestId />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                    No revenue items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <PaymentsPagination
          pagination={ledgerPagination}
          paginationItems={ledgerPaginationItems}
          paginationLabel={ledgerPaginationLabel}
          onPageChange={setLedgerPage}
        />
      </Box>
    </>
  );

  const renderPayoutFilters = () => (
    <Box sx={{ ...filterToolbarSx, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <Box sx={{ ...fieldBlockStyles, flex: 1, minWidth: 200, maxWidth: 420 }}>
        <Typography sx={fieldLabelStyles}>Wallet address</Typography>
        <TextField
          placeholder="Filter by wallet (contains)"
          value={payoutRecipient}
          onChange={(e) => {
            setPayoutRecipient(e.target.value);
            setPayoutWalletPage(1);
            setPayoutMicrojobWalletPage(1);
            setPayoutLineItemsPage(1);
          }}
          fullWidth
          sx={formFieldStyles}
        />
      </Box>
      <Box sx={{ ...fieldBlockStyles, minWidth: 180 }}>
        <Typography sx={fieldLabelStyles}>Recipient type</Typography>
        <Select
          value={payoutRecipientType}
          onChange={(e) => {
            setPayoutRecipientType(e.target.value);
            setPayoutWalletPage(1);
            setPayoutMicrojobWalletPage(1);
            setPayoutLineItemsPage(1);
          }}
          displayEmpty
          fullWidth
          renderValue={(v) => {
            if (!v) return 'All types';
            if (v === 'lp_holder') return 'LP holder';
            if (v === 'order_handler') return 'Order handler';
            if (v === 'microjob') return 'Microjob';
            if (v === 'treasury') return 'DAO treasury';
            if (v === 'platform') return 'Platform';
            return v;
          }}
          sx={makeSelectSx(false, !!payoutRecipientType)}
          MenuProps={selectMenuProps}
        >
          <MenuItem value="">All types</MenuItem>
          <MenuItem value="lp_holder">LP holder</MenuItem>
          <MenuItem value="order_handler">Order handler</MenuItem>
          <MenuItem value="microjob">Microjob</MenuItem>
          <MenuItem value="treasury">DAO treasury</MenuItem>
          <MenuItem value="platform">Platform</MenuItem>
        </Select>
      </Box>
    </Box>
  );

  const renderRecipientWalletTable = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {showVaultWalletTable && (
        <Box>
          <Typography sx={{ ...previewSummaryLabelSx, textTransform: 'none', letterSpacing: 0, mb: 1 }}>
            {VAULT_WALLET_SOURCE_LABEL}
          </Typography>
          <Box sx={tableCardStyles}>
            <TableContainer component={Box} sx={{ ...tableContainerStyles, maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Wallet</TableCell>
                    <TableCell>Types</TableCell>
                    <TableCell align="right">Allocated (USDT)</TableCell>
                    <TableCell align="right">Claimed</TableCell>
                    <TableCell align="right">Pending</TableCell>
                    <TableCell align="center">Claims</TableCell>
                    <TableCell>Last claim</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading || claimSummaryLoading ? (
                    renderTableLoadingRow(7, 'Loading payout data...')
                  ) : recipientRowsPage.length ? (
                    recipientRowsPage.map((row) => (
                      <TableRow key={`vault-${row.id}`} hover>
                        <TableCell>
                          <Typography sx={walletAddressCellSx}>{row.recipientAddress || '—'}</Typography>
                        </TableCell>
                        <TableCell>{vaultWalletTypeLabels(row)}</TableCell>
                        <TableCell align="right">{formatUsdt(row.vaultAllocatedTotal)}</TableCell>
                        <TableCell align="right">{formatUsdt(row.vaultClaimed)}</TableCell>
                        <TableCell align="right">{formatUsdt(row.vaultPending ?? '0')}</TableCell>
                        <TableCell align="center">{row.vaultClaimCount ?? 0}</TableCell>
                        <TableCell>{formatDateTime(row.vaultLastClaimedAt)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                        No Vault recipients from executed cycles yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <PaymentsPagination
              pagination={payoutWalletPagination}
              paginationItems={payoutWalletPaginationItems}
              paginationLabel={payoutWalletPaginationLabel}
              onPageChange={setPayoutWalletPage}
            />
          </Box>
        </Box>
      )}

      {showMicrojobWalletTable && (
        <Box>
          <Typography sx={{ ...previewSummaryLabelSx, textTransform: 'none', letterSpacing: 0, mb: 1 }}>
            {MICROJOB_WALLET_SOURCE_LABEL}
          </Typography>
          <Box sx={tableCardStyles}>
            <TableContainer component={Box} sx={{ ...tableContainerStyles, maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Wallet</TableCell>
                    <TableCell align="right">Allocated (USDT)</TableCell>
                    <TableCell align="right">Claimed</TableCell>
                    <TableCell align="right">Pending</TableCell>
                    <TableCell align="center">Claims</TableCell>
                    <TableCell>Last claim</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading || claimSummaryLoading ? (
                    renderTableLoadingRow(6, 'Loading payout data...')
                  ) : microjobRecipientRowsPage.length ? (
                    microjobRecipientRowsPage.map((row) => (
                      <TableRow key={`microjob-${row.id}`} hover>
                        <TableCell>
                          <Typography sx={walletAddressCellSx}>{row.recipientAddress || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">{formatUsdt(row.microjobAllocatedTotal)}</TableCell>
                        <TableCell align="right">{formatUsdt(row.microjobClaimed)}</TableCell>
                        <TableCell align="right">{formatUsdt(row.microjobPending ?? '0')}</TableCell>
                        <TableCell align="center">{row.microjobClaimCount ?? 0}</TableCell>
                        <TableCell>{formatDateTime(row.microjobLastClaimedAt)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                        No Microjob recipients from executed cycles yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <PaymentsPagination
              pagination={payoutMicrojobWalletPagination}
              paginationItems={payoutMicrojobWalletPaginationItems}
              paginationLabel={payoutMicrojobWalletPaginationLabel}
              onPageChange={setPayoutMicrojobWalletPage}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderPayoutLineItems = () => (
    <Box sx={tableCardStyles}>
      <TableContainer component={Box} sx={{ ...tableContainerStyles, maxHeight: 480 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cycle period</TableCell>
              <TableCell>Recipient type</TableCell>
              <TableCell>Recipient wallet</TableCell>
              <TableCell align="right">Payout amount (USDT)</TableCell>
              <TableCell>Split version</TableCell>
              <TableCell>Distribution tx hash</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              renderTableLoadingRow(6, 'Loading payout line items...')
            ) : payoutRowsPage.length ? (
              payoutRowsPage.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.cycle}</TableCell>
                  <TableCell>{row.recipientTypeLabel}</TableCell>
                  <TableCell>
                    <Typography sx={walletAddressCellSx}>{row.recipientAddress || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatUsdt(row.amount)}</TableCell>
                  <TableCell>{row.splitVersion != null ? `v${row.splitVersion}` : '—'}</TableCell>
                  <TableCell>
                    <Typography sx={walletAddressCellSx}>{row.txHash || '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: colors.textSecondary }}>
                  No executed payout rows yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaymentsPagination
        pagination={payoutLineItemsPagination}
        paginationItems={payoutLineItemsPaginationItems}
        paginationLabel={payoutLineItemsPaginationLabel}
        onPageChange={setPayoutLineItemsPage}
      />
    </Box>
  );

  const renderPayoutLedger = () => (
    <>
      <Tabs
        value={payoutSubView}
        onChange={(e, v) => setPayoutSubView(v)}
        sx={{ ...subTabSx, mb: 2 }}
      >
        <Tab label="Line items" />
        <Tab label="By wallet" />
      </Tabs>
      {renderPayoutFilters()}
      {payoutSubView === 0 ? renderPayoutLineItems() : renderRecipientWalletTable()}
    </>
  );

  return (
    <Box>
      <Box sx={tabHeaderRowSx}>
        <Typography variant="h6" sx={pageTitleSx}>
          Transparency
        </Typography>
      </Box>

      <Typography sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif', mb: 2, maxWidth: 800 }}>
        Revenue and payout logs for this DAO — audit inflows, per-wallet claim status, and distribution line items.
      </Typography>

      {error && (
        <Alert severity="error" sx={tableAlertErrorSx}>
          {error}
        </Alert>
      )}

      <Tabs
        value={view}
        onChange={(e, v) => setView(v)}
        sx={{
          mb: 2,
          minHeight: 0,
          borderBottom: `1px solid ${colors.border}`,
          '& .MuiTab-root': {
            color: colors.textSecondary,
            textTransform: 'none',
            fontFamily: 'Questrial, sans-serif',
            minHeight: 40,
          },
          '& .Mui-selected': { color: colors.accent },
          '& .MuiTabs-indicator': { backgroundColor: colors.accent },
        }}
      >
        <Tab label="Dashboard" />
        <Tab label="Revenue ledger" />
        <Tab label="Payout ledger" />
      </Tabs>

      {view === 0 && renderDashboard()}
      {view === 1 && renderRevenueLedger()}
      {view === 2 && renderPayoutLedger()}
    </Box>
  );
};

export default TransparencySection;
